import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { api, apiUrl, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card } from "../../components/ui-kit";

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [proofs, setProofs] = useState({});
  const [commissionNotes, setCommissionNotes] = useState({});

  const load = () => {
    Promise.all([api.get("/admin/finance"), api.get("/admin/payouts")])
      .then(([finance, payoutRows]) => {
        setData(finance.data);
        setPayouts(payoutRows.data || []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const approvePayout = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}/approve`, {
        proof_reference: proofs[id] || "",
        note: "Paiement valide par admin",
      });
      toast.success("Retrait marque paye");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectPayout = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}/reject`, {
        note: proofs[id] || "Retrait refuse par admin",
      });
      toast.success("Retrait refuse");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const settleCommission = async (id) => {
    try {
      await api.post(`/admin/commission-ledger/${id}/settle`, {
        method: "cash",
        reference: commissionNotes[id] || "",
        note: "Commission directe reglee",
      });
      toast.success("Commission marquee payee");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const metrics = [
    ["Courses terminees", data?.completed_rides || 0],
    ["Chiffre brut", formatCDF(data?.gross_revenue || 0)],
    ["Revenu plateforme", formatCDF(data?.platform_revenue || 0)],
    ["Commandes marketplace", data?.marketplace_orders || 0],
    ["Ventes partenaires", formatCDF(data?.marketplace_gross || 0)],
    ["Commission marketplace", formatCDF(data?.marketplace_commission || 0)],
    ["Commissions directes dues", formatCDF(data?.direct_commission_due_total || 0)],
    ["Dues chauffeurs", formatCDF(data?.direct_commission_due_driver || 0)],
    ["Dues marchands", formatCDF(data?.direct_commission_due_merchant || 0)],
    ["Commissions directes payees", formatCDF(data?.direct_commission_paid_total || 0)],
    ["Net partenaires", formatCDF(data?.marketplace_merchant_net || 0)],
    ["Gains chauffeurs", formatCDF(data?.driver_earnings || 0)],
    ["Retraits payes", formatCDF(data?.payout_paid_total || 0)],
    ["Retraits en attente", formatCDF(data?.payout_pending_total || 0)],
    ["Remboursements", formatCDF(data?.refund_total || 0)],
    ["Net plateforme", formatCDF(data?.net_platform_revenue || 0)],
  ];
  return (
    <AdminShell title="Finance">
      <div className="admin-page-header">
        <div className="min-w-0">
          <h1 className="responsive-title font-heading text-3xl font-black tracking-tighter sm:text-4xl">Finance & commissions</h1>
          <p className="text-sm text-muted-foreground">Revenus plateforme, gains chauffeurs et exports.</p>
        </div>
        <div className="admin-actions">
          <a href={apiUrl("/admin/finance/export.csv")} target="_blank" rel="noreferrer">
            <Button variant="outline">CSV</Button>
          </a>
          <a href={apiUrl("/admin/finance/export.pdf")} target="_blank" rel="noreferrer">
            <Button variant="outline">PDF</Button>
          </a>
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {metrics.map(([label, value]) => (
          <Card key={label} className="admin-metric-card p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-2 break-words font-heading text-2xl font-black">{value}</div>
          </Card>
        ))}
      </div>
      <Card className="mt-6 overflow-x-auto">
        <div className="border-b border-white/10 p-4">
          <h2 className="font-heading text-xl font-black">Commissions directes</h2>
          <p className="text-xs text-muted-foreground">
            Paiements faits directement entre client et chauffeur/marchand. NEXORA suit uniquement sa commission due.
          </p>
        </div>
        <table className="admin-table w-full text-sm">
          <thead className="border-b border-white/10">
            <tr>
              <th className="p-3 text-left">Source</th>
              <th className="p-3 text-left">Qui doit</th>
              <th className="p-3 text-left">Brut</th>
              <th className="p-3 text-left">Commission</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-left">Reference</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {(data?.commission_ledger || []).map((entry) => (
              <tr key={entry.id} className="border-b border-white/10 align-top">
                <td className="p-3">
                  <div className="font-bold">{entry.source_type === "ride" ? "Course" : "Commande"}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{entry.source_id}</div>
                </td>
                <td className="p-3">
                  <div className="font-bold">{entry.payer_name || "-"}</div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{entry.payer_type}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{entry.payer_phone || "-"}</div>
                </td>
                <td className="p-3 font-mono">{formatCDF(entry.gross_amount || 0)}</td>
                <td className="p-3 font-mono font-bold text-cyan-300">{formatCDF(entry.commission_amount || 0)}</td>
                <td className="p-3">
                  <Badge tone={entry.status === "PAID" ? "success" : "warning"}>{entry.status}</Badge>
                </td>
                <td className="p-3">
                  {entry.status === "DUE" ? (
                    <input
                      className="w-52 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs"
                      placeholder="Reference ou note"
                      value={commissionNotes[entry.id] || ""}
                      onChange={(e) => setCommissionNotes((v) => ({ ...v, [entry.id]: e.target.value }))}
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      {entry.settlement_reference || entry.settlement_note || "-"}
                    </div>
                  )}
                </td>
                <td className="p-3">
                  {entry.status === "DUE" ? (
                    <Button variant="secondary" onClick={() => settleCommission(entry.id)}>Marquer paye</Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {entry.settled_at ? new Date(entry.settled_at).toLocaleString("fr-FR") : "-"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {(data?.commission_ledger || []).length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Aucune commission directe
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      <Card className="mt-6 overflow-x-auto">
        <table className="admin-table w-full text-sm">
          <thead className="border-b border-white/10">
            <tr>
              <th className="p-3 text-left">Chauffeur</th>
              <th className="p-3 text-left">Courses</th>
              <th className="p-3 text-left">Brut</th>
              <th className="p-3 text-left">Gain chauffeur</th>
              <th className="p-3 text-left">Commission</th>
            </tr>
          </thead>
          <tbody>
            {(data?.by_driver || []).map((d) => (
              <tr key={d.driver_id} className="border-b border-white/10">
                <td className="p-3 font-bold">{d.driver_name}</td>
                <td className="p-3"><Badge>{d.rides}</Badge></td>
                <td className="p-3 font-mono">{formatCDF(d.gross)}</td>
                <td className="p-3 font-mono">{formatCDF(d.driver_earnings)}</td>
                <td className="p-3 font-mono">{formatCDF(d.platform_commission)}</td>
              </tr>
            ))}
            {(data?.by_driver || []).length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Aucun revenu chauffeur
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      <Card className="mt-6 overflow-x-auto">
        <div className="border-b border-white/10 p-4">
          <h2 className="font-heading text-xl font-black">Demandes de retrait chauffeur</h2>
          <p className="text-xs text-muted-foreground">Validation admin avec reference ou preuve de paiement.</p>
        </div>
        <table className="admin-table w-full text-sm">
          <thead className="border-b border-white/10">
            <tr>
              <th className="p-3 text-left">Chauffeur</th>
              <th className="p-3 text-left">Montant</th>
              <th className="p-3 text-left">Compte</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-left">Preuve / note</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((p) => (
              <tr key={p.id} className="border-b border-white/10 align-top">
                <td className="p-3">
                  <div className="font-bold">{p.driver_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{p.driver_phone}</div>
                </td>
                <td className="p-3 font-mono">{formatCDF(p.amount)}</td>
                <td className="p-3 text-xs">{p.method}<br />{p.account_name}<br />{p.account_number}</td>
                <td className="p-3"><Badge tone={p.status === "PAID" ? "success" : p.status === "REJECTED" ? "danger" : "warning"}>{p.status}</Badge></td>
                <td className="p-3">
                  {p.status === "PENDING" ? (
                    <input
                      className="w-52 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs"
                      placeholder="Reference transaction"
                      value={proofs[p.id] || ""}
                      onChange={(e) => setProofs((v) => ({ ...v, [p.id]: e.target.value }))}
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">{p.proof_reference || p.admin_note || "-"}</div>
                  )}
                </td>
                <td className="p-3">
                  {p.status === "PENDING" ? (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="secondary" onClick={() => approvePayout(p.id)}>Payer</Button>
                      <Button variant="danger" onClick={() => rejectPayout(p.id)}>Refuser</Button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">{p.updated_at ? new Date(p.updated_at).toLocaleString("fr-FR") : "-"}</span>
                  )}
                </td>
              </tr>
            ))}
            {payouts.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Aucun retrait chauffeur
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </AdminShell>
  );
}
